"use client";

import { type ComponentType, useState, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import {
  MessageCircleQuestion,
  Radar,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { toast } from "sonner";
import { UserMenu } from "@/components/auth/user-menu";
import { useSidekickStore } from "@/lib/stores/sidekick-store";
import { useFullscreen } from "@/hooks/use-fullscreen";
import { suggestClusters } from "@/lib/hive-spaces-client";
import { runDiscovery } from "@/lib/api/mapper";

type IconComponent = ComponentType<{ className?: string }>;

type NavItem = {
  href: string;
  label: string;
  icon?: IconComponent;
  iconSrc?: string;
  tourKey: string;
};

const navItems: NavItem[] = [
  { 
    href: "/mcore", 
    label: "mCore", 
    iconSrc: "/icons/mc_icon.svg",
    tourKey: "mCore" 
  },
  {
    href: "/connection-studio",
    label: "Connection Studio",
    iconSrc: "/icons/intelify-ico.svg",
    tourKey: "Connection Studio",
  },
  {
    href: "/hive-spaces",
    label: "mSpace",
    iconSrc: "/icons/mspace-logo.svg",
    tourKey: "mSpace",
  },
  {
    href: "/campus-spaces",
    label: "Campus",
    iconSrc: "/icons/ms_icon.svg",
    tourKey: "Campus",
  },
];

const tourClassMap: Record<string, string> = {
  mCore: "mhive-nav-mcore",
  "Connection Studio": "mhive-nav-studio",
};

export function Navigation() {
  const pathname = usePathname();
  const isHomePage = pathname === "/";
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();

  return (
    <nav className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#070a12]/95 backdrop-blur-2xl">
      <div className="flex h-11 items-stretch px-5">
        {/* ── Logo ── */}
        <Link href="/" className="mhive-logo flex items-center pr-1">
          <Image
            src="/mstro_logo.svg"
            alt="MSTRO mHive"
            width={38}
            height={25}
            priority
            className="opacity-85 transition-opacity duration-200 hover:opacity-100"
          />
        </Link>

        {!isHomePage && (
          <>
            {/* Separator */}
            <div className="mx-3 flex items-center">
              <div className="h-3.5 w-px bg-white/[0.08]" />
            </div>

            {/* ── Nav links ── */}
            <div className="flex flex-1 items-stretch overflow-hidden">
              <div className="flex items-stretch">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive =
                    pathname === item.href ||
                    pathname?.startsWith(`${item.href}/`);

                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      aria-current={isActive ? "page" : undefined}
                      className={cn(
                        "group relative flex items-center gap-1.5 px-3 text-[11.5px] font-medium tracking-tight transition-all duration-200",
                        tourClassMap[item.tourKey],
                        isActive
                          ? "text-white/90"
                          : "text-white/50 hover:text-white/60",
                      )}
                    >
                      {item.iconSrc ? (
                        <Image
                          src={item.iconSrc}
                          alt=""
                          aria-hidden="true"
                          width={13}
                          height={13}
                          className={cn(
                            "flex-shrink-0 transition-all duration-200",
                            isActive ? "opacity-100" : "opacity-50 group-hover:opacity-70",
                          )}
                          style={isActive ? { filter: "drop-shadow(0 0 4px rgba(0,0,0,0.9))" } : undefined}
                        />
                      ) : Icon ? (
                        <Icon
                          className={cn(
                            "h-[13px] w-[13px] flex-shrink-0 transition-all duration-200",
                            isActive
                              ? "text-teal-400/80 opacity-100"
                              : "opacity-50 group-hover:opacity-70",
                          )}
                        />
                      ) : null}
                      <span className="whitespace-nowrap">{item.label}</span>

                      {/* Active indicator — bottom accent line */}
                      {isActive && (
                        <span className="absolute inset-x-2.5 -bottom-px h-[1.5px] rounded-full bg-teal-400/60 shadow-[0_0_8px_rgba(45,212,191,0.3)]" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          </>
        )}
        {/* ── Right utilities ── */}
        <div className="ml-auto flex items-center gap-0.5">
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            aria-pressed={isFullscreen}
            className="flex h-7 w-7 items-center justify-center rounded-md text-white/55 transition-all duration-200 hover:bg-white/[0.05] hover:text-white/70"
          >
            {isFullscreen ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
          </button>
          <UserMenu />
        </div>
      </div>
    </nav>
  );
}

function DiscoverButton() {
  const [running, setRunning] = useState(false);

  const handleDiscover = useCallback(async () => {
    if (running) return;
    setRunning(true);
    try {
      await Promise.allSettled([suggestClusters(), runDiscovery()]);
      toast.success("Discovery complete");
    } catch {
      toast.error("Discovery failed");
    } finally {
      setRunning(false);
    }
  }, [running]);

  return (
    <button
      type="button"
      onClick={handleDiscover}
      disabled={running}
      aria-label="Rediscover connections & data"
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md transition-all duration-200",
        running
          ? "text-teal-400/80 bg-teal-500/10"
          : "text-white/55 hover:bg-white/[0.05] hover:text-white/70",
      )}
    >
      <Radar className={cn("h-3.5 w-3.5", running && "animate-pulse")} />
    </button>
  );
}

function AskMHButton() {
  const toggle = useSidekickStore((s) => s.toggle);
  const isOpen = useSidekickStore((s) => s.isOpen);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Ask mH"
      aria-pressed={isOpen}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md transition-all duration-200",
        isOpen
          ? "bg-teal-500/10 text-teal-400/80"
          : "text-white/55 hover:bg-white/[0.05] hover:text-white/70",
      )}
    >
      <MessageCircleQuestion className="h-3.5 w-3.5" />
    </button>
  );
}
